import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import { Col, Form, Row } from 'react-bootstrap';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Cobro, cobroInicial } from 'data/project-management/Cobro';
import axios, { AxiosError } from 'axios';
import { useAuth } from 'providers/AuthProvider';
import { formatMoney } from 'helpers/utils';

interface CobroDetailProps {
  // Props para modo embebido (Creación en MovimientoComponent)
  isEmbedded?: boolean;
  cobroData?: Cobro;
  onChangeCobro?: (updatedCobro: Cobro) => void;
  onSubmitEmbedded?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isSubmittingEmbedded?: boolean;
  // Nuevas Props para el manejo como Modal Standalone
  cobroId?: string | number;
  isModal?: boolean;
  onClose?: () => void;
}

const CobroDetail: React.FC<CobroDetailProps> = ({
  isEmbedded = false,
  cobroData,
  onChangeCobro,
  onSubmitEmbedded,
  isSubmittingEmbedded = false,
  cobroId,
  isModal = false,
  onClose
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: urlId } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
  
  const fetchId = cobroId || urlId; // Tomamos el id por Prop o por URL

  // Estado interno para modo Standalone (Edición por URL o Modal)
  const [cobroInternal, setCobroInternal] = useState<Cobro>(cobroInicial);
  const [isInitiallyCobrado, setIsInitiallyCobrado] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cobro = isEmbedded && cobroData ? cobroData : cobroInternal;
  const submitting = isEmbedded ? isSubmittingEmbedded : isSubmitting;

  const updateCobro = (updater: (prev: Cobro) => Cobro) => {
    if (isEmbedded && onChangeCobro) {
      onChangeCobro(updater(cobro));
    } else {
      setCobroInternal(prev => updater(prev));
    }
  };

  // Fetch Cobro en modo Standalone
  useEffect(() => {
    if (isEmbedded) return;

    const fetchCobro = async () => {
      try {
        if (!fetchId || isNaN(Number(fetchId))) {
          console.error('Invalid cobro ID:', fetchId);
          if (!isModal) navigate('/app/apps/project-management/cobros-list-view');
          return;
        }
        const response = await axios.get(`${apiUrl}/cobros/id/${fetchId}`);
        setCobroInternal(response.data);
        setIsInitiallyCobrado(response.data.estado === 'Cobrado');
      } catch (error) {
        console.error('Error fetching cobro:', (error as AxiosError).response?.data);
        if (!isModal) navigate('/app/apps/project-management/cobros-list-view');
      }
    };
    fetchCobro();
  }, [fetchId, isEmbedded, apiUrl, navigate, isModal]);

  const handleMontoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = Number(event.target.value.replace(/\D/g, '')) || 0;
    updateCobro(prev => {
      const esSRT = prev.proc?.substring(0, 3) === 'SRT'; 
      const porcentajePCL = esSRT ? 0.1 : 0.2;
      const nuevoPCL = prev.capital_honorarios === 'Capital' || prev.quien_cobra === 'CASH' 
        ? Math.round(numericValue * porcentajePCL) 
        : prev.PCL;
      return { ...prev, monto: numericValue, PCL: nuevoPCL };
    });
  };

  const handleCHchange = (event: React.ChangeEvent<HTMLSelectElement>) => updateCobro(prev => ({ ...prev, capital_honorarios: event.target.value }));
  const handlePCLChange = (event: React.ChangeEvent<HTMLInputElement>) => updateCobro(prev => ({ ...prev, PCL: Number(event.target.value.replace(/\D/g, '')) || 0 }));
  const handleQuienCobraChange = (event: React.ChangeEvent<HTMLSelectElement>) => updateCobro(prev => ({ ...prev, quien_cobra: event.target.value }));
  const handleSelectChangeTranfeOLJ = (event: React.ChangeEvent<HTMLSelectElement>) => updateCobro(prev => ({ ...prev, libranza_judicial_transferencia_directa: event.target.value }));
  const handleFechadepagoChange = (dates: Date[]) => updateCobro(prev => ({ ...prev, fecha_de_pago: dates && dates.length > 0 ? dates[0] : new Date() }));
  const handleSelectChangeCobrado = (event: React.ChangeEvent<HTMLSelectElement>) => updateCobro(prev => ({ ...prev, estado: event.target.value }));
  const handleNotasChange = (event: React.ChangeEvent<HTMLInputElement>) => updateCobro(prev => ({ ...prev, notas: event.target.value }));

  const handleCancel = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate('/app/apps/project-management/cobros-list-view');
    }
  };

  const handleSubmitCobroStandalone = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const confirmSave = window.confirm(
      `Estás guardando este cobro:\n
      Concepto: ${cobro.capital_honorarios}\n
      Monto: $ ${formatMoney(cobro.monto || 0)}\n
      PCL: $ ${formatMoney(cobro.PCL || 0)}\n
      Estado: ${cobro.estado}\n
      Cobra: ${cobro.quien_cobra || 'efectivo'}\n
      Modo de pago: ${cobro.libranza_judicial_transferencia_directa}\n
      Notas: ${cobro.notas}\n\n
      ¿Confirmas?`
    );

    if (!confirmSave) {
      setIsSubmitting(false);
      return;
    }

    try {
      const { cobro_id } = cobro;
      const cobroData = { ...cobro, cobro_id: undefined };
      await axios.put(`${apiUrl}/cobros/update/${cobro_id}`, { cobroData });
      setIsSubmitting(false);
      
      if (isModal && onClose) {
        onClose();
      } else {
        navigate('/app/apps/project-management/cobros-list-view');
      }
    } catch (error) {
      console.error('Error updating cobro:', (error as AxiosError).response?.data || error);
      alert('Error al guardar: ' + ((error as AxiosError).response?.data || 'Error en el servidor'));
      setIsSubmitting(false);
    }
  };

  return (
    <div className='p-4'>
      {!isEmbedded && <h2 className="mb-4">Cobro: {cobro.nombre}</h2>}

      <div id="saleTranfe" className="mb-4 p-3 bg-light border rounded">
        <Row className="g-3">
          {/* ... (Todo tu formulario de Cobro igual) ... */}
          <Col md={4}>
            <Form.Label>Monto</Form.Label>
            {isInitiallyCobrado ? (
              <div>{formatMoney(cobro.monto || 0)}</div>
            ) : (
              <Form.Control size="lg" type="text" placeholder="$ 0" value={cobro.monto ? formatMoney(cobro.monto) : ''} onChange={handleMontoChange} />
            )}
          </Col>

          <Col xs={6} md={4}>
            <Form.Label>Capital/Honorarios</Form.Label>
            {isInitiallyCobrado ? (
              <div>{cobro.capital_honorarios}</div>
            ) : (
              <Form.Select size="lg" value={cobro.capital_honorarios || ''} onChange={handleCHchange}>
                <option value="Capital">Capital</option>
                <option value="Honorarios">Honorarios</option>
              </Form.Select>
            )}
          </Col>

          <Col xs={6} md={4}>
            <Form.Label>PCL</Form.Label>
            {isInitiallyCobrado ? (
              <div>{formatMoney(cobro.PCL || 0)}</div>
            ) : (
              <Form.Control size="lg" type="text" placeholder="$ 0" value={cobro.PCL ? formatMoney(cobro.PCL) : ''} onChange={handlePCLChange} />
            )}
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Quién Cobra</Form.Label>
            {isInitiallyCobrado ? (
              <div>{cobro.quien_cobra || 'Efectivo'}</div>
            ) : (
              <Form.Select size="lg" onChange={handleQuienCobraChange} value={cobro.quien_cobra || ''}>
                <option value="CAP">CAP</option><option value="CASH">CASH</option>
                <option value="EA">EA</option><option value="GEO">GEO</option>
                <option value="IS">IS</option><option value="IS_CASH">IS CASH</option>
                <option value="ISV">ISV</option><option value="LA">LA</option>
                <option value="MVP">MVP</option><option value="SAG">SAG</option>
                <option value="SUCESION">SUCESION</option>
              </Form.Select>
            )}
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Libranza Judicial / Transferencia Directa</Form.Label>
            {isInitiallyCobrado ? (
              <div>{cobro.libranza_judicial_transferencia_directa}</div>
            ) : (
              <Form.Select size="lg" onChange={handleSelectChangeTranfeOLJ} value={cobro.libranza_judicial_transferencia_directa || ''}>
                <option value="Libranza Judicial">Libranza Judicial</option>
                <option value="Transferencia Directa">Transferencia Directa</option>
              </Form.Select>
            )}
          </Col>

          <Col sm={6} md={4}>
            <Form.Label>Fecha de Pago</Form.Label>
            {isInitiallyCobrado ? (
              <div>{cobro.fecha_de_pago ? new Date(cobro.fecha_de_pago).toLocaleDateString() : ''}</div>
            ) : (
              <DatePicker
                value={cobro.fecha_de_pago ? new Date(cobro.fecha_de_pago) : new Date()}
                onChange={handleFechadepagoChange}
                render={(_, ref) => (
                  <Form.Floating>
                    <Form.Control type="date" ref={ref} id="fechadepago" placeholder="Fecha de Pago" />
                  </Form.Floating>
                )}
              />
            )}
          </Col>

          <Col xs={12} md={4}>
            <Form.Label>Estado</Form.Label>
            {isInitiallyCobrado ? (
              <div>{cobro.estado}</div>
            ) : (
              <Form.Select size="lg" onChange={handleSelectChangeCobrado} value={cobro.estado || ''}>
                <option value="Pendiente">Pendiente</option>
                <option value="Cobrado">Cobrado</option>
                <option value="Cancelado">Cancelado</option>
                <option value="Actor Moroso">Actor Moroso</option>
              </Form.Select>
            )}
          </Col>

          <Col md={4}>
            <Form.Label>Notas</Form.Label>
            {isInitiallyCobrado ? (
              <div>{cobro.notas || ''}</div>
            ) : (
              <Form.Control size="lg" type="text" value={cobro.notas || ''} onChange={handleNotasChange} />
            )}
          </Col>

          <Col md={4} className="d-flex align-items-end">
            {!isInitiallyCobrado && (
              <Button
                variant="primary"
                className="px-5 me-2"
                onClick={isEmbedded ? onSubmitEmbedded : handleSubmitCobroStandalone}
                disabled={submitting}
              >
                {submitting ? 'Guardando...' : 'Guardar Cobro'}
              </Button>
            )}
            {!isEmbedded && (
              <Button
                variant="secondary"
                className="px-5"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancelar
              </Button>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CobroDetail;